import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useActivateApprover,
  useDeactivateApprover,
  useDeleteApprover,
  useFinanceSettingsStore,
  useUpdateApprover
} from "features/finances";
import { useUsers } from "features/users";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import * as yup from "yup";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  background-color: transparent;
`;

const approverSchema = yup.object({
  approver: yup.string().required("Approver is required"),
  approverLevel: yup.string().required("Approval level is required"),
  approvalLimitPerRequest: yup
    .number()
    .typeError("Approval limit per request must be a number")
    .required("Approval limit per request is required"),
  approvalLimitPerMonth: yup
    .number()
    .typeError("Approval limit per month must be a number")
    .required("Approval limit per month is required")
});

export function UpdateApproverModal({ data }) {
  const updateApproverOpen = useFinanceSettingsStore(
    state => state.updateApproverOpen
  );
  const closeUpdateApproverModal = useFinanceSettingsStore(
    state => state.closeUpdateApproverModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const usersQuery = useUsers({
    churchId: selectedChurch?.id
  });
  const updateApproverMutation = useUpdateApprover();
  const deleteApproverMutation = useDeleteApprover();
  const deactivateApproverMutation = useDeactivateApprover();
  const activateApproverMutation = useActivateApprover();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      approver: data?.approver_id || "",
      approverLevel: data?.approver_level || "",
      approvalLimitPerRequest: data?.approval_limit_per_request || 0,
      approvalLimitPerMonth: data?.approval_limit_per_month || 0
    },
    resolver: yupResolver(approverSchema)
  });

  useEffect(() => {
    if (data) {
      setValue("approver", data?.approver_id || "");
      setValue("approverLevel", data?.approver_level || "");
      setValue(
        "approvalLimitPerRequest",
        data?.approval_limit_per_request || 0
      );
      setValue("approvalLimitPerMonth", data?.approval_limit_per_month || 0);
    }
  }, [data]);

  const onSubmit = values => {
    const approverData = {
      approver_id: values.approver,
      approval_limit_per_request: values.approvalLimitPerRequest,
      approval_limit_per_month: values.approvalLimitPerMonth,
      approver_level: values.approverLevel
    };

    updateApproverMutation.mutate(
      {
        churchId: selectedChurch?.id,
        approverData,
        approverId: data?.id
      },
      {
        onSuccess: () => {
          reset();
        }
      }
    );
  };

  const approverLevel = watch("approverLevel");

  return (
    <BaseModal
      title="Update Approver"
      isOpen={updateApproverOpen}
      onClose={closeUpdateApproverModal}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="row" justifyContent="flex-start" spacing={2}>
            <Button
              flavor="neutral"
              size="small"
              onClick={() => {
                deleteApproverMutation.mutate({
                  churchId: selectedChurch?.id,
                  approverId: data?.id
                });
              }}
              disabled={
                updateApproverMutation.isLoading ||
                deleteApproverMutation.isLoading ||
                deactivateApproverMutation.isLoading ||
                activateApproverMutation.isLoading
              }
            >
              Delete
            </Button>
            {data?.approver_status === "ACTIVE" ? (
              <Button
                flavor="neutral"
                size="small"
                onClick={() => {
                  deactivateApproverMutation.mutate({
                    churchId: selectedChurch?.id,
                    approverId: data?.id,
                    approverData: {}
                  });
                }}
                disabled={
                  updateApproverMutation.isLoading ||
                  deleteApproverMutation.isLoading ||
                  deactivateApproverMutation.isLoading ||
                  activateApproverMutation.isLoading
                }
              >
                Deactivate
              </Button>
            ) : (
              <Button
                flavor="neutral"
                size="small"
                onClick={() => {
                  activateApproverMutation.mutate({
                    churchId: selectedChurch?.id,
                    approverId: data?.id,
                    approverData: {}
                  });
                }}
                disabled={
                  updateApproverMutation.isLoading ||
                  deleteApproverMutation.isLoading ||
                  deactivateApproverMutation.isLoading ||
                  activateApproverMutation.isLoading
                }
              >
                Activate
              </Button>
            )}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              flavor="white"
              size="small"
              onClick={closeUpdateApproverModal}
              disabled={
                updateApproverMutation.isLoading ||
                deleteApproverMutation.isLoading ||
                deactivateApproverMutation.isLoading ||
                activateApproverMutation.isLoading
              }
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleSubmit(onSubmit)}
              disabled={
                updateApproverMutation.isLoading ||
                deleteApproverMutation.isLoading ||
                deactivateApproverMutation.isLoading ||
                activateApproverMutation.isLoading
              }
            >
              Update Approver
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="name">Approver Name</Label>
        <Select id="name" {...register("approver")}>
          {usersQuery.data?.result?.map(user => (
            <option key={user.id} value={user.id}>
              {user.first_name} {user.last_name}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="approver"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="approverLevel">Approval Level</Label>
        <Select id="approverLevel" {...register("approverLevel")}>
          <option value="" disabled>
            Select approval level
          </option>
          <option value={1}>First Level Approver</option>
          <option value={2}>Second Level Approver</option>
          <option value={3}>Third Level Approver</option>
        </Select>
        <ErrorMessage
          errors={errors}
          name="approverLevel"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      {approverLevel && (
        <Alert severity="info" sx={{ fontSize: "12px" }}>
          {String(approverLevel) === "1" && "Initial approval"}
          {String(approverLevel) === "2" && "Mid-level approval"}
          {String(approverLevel) === "3" && "Final approval"}
        </Alert>
      )}
      <Field>
        <Label htmlFor="approvalLimitPerRequest">
          Maximum Approve per request in ETB
        </Label>
        <Controller
          name="approvalLimitPerRequest"
          control={control}
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              id="approvalLimitPerRequest"
              thousandSeparator
              allowNegative={false}
              onValueChange={values => {
                field.onChange(values.floatValue);
              }}
              value={field.value}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="approvalLimitPerRequest"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="approvalLimitPerMonth">
          Maximum Approve per month in ETB
        </Label>
        <Controller
          name="approvalLimitPerMonth"
          control={control}
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              id="approvalLimitPerMonth"
              thousandSeparator
              allowNegative={false}
              onValueChange={values => {
                field.onChange(values.floatValue);
              }}
              value={field.value}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="approvalLimitPerMonth"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>

      <MutationError error={updateApproverMutation.error} sx={{ mt: 2 }} />
    </BaseModal>
  );
}
